import React, { FormEvent, useEffect, useRef, useState } from 'react'
import ReactLoading from 'react-loading'

import jasnaLogo from '@/assets/jasnaLogo.svg'
import messagesIcon from '@/assets/messagesIcon.svg'
import closeIcon from '@/assets/closeIcon.svg'
import { useAppDispatch, useAppSelector } from '@/store'
import { useSendMessageMutation, useSendMessageWithThreadMutation } from '@/store/chat-api'
import { addMessage, setTyping, clearMessages } from '@/store/chat.slice'
import { Message, MessageType } from '@/store/chat.types'
import MessageContainer from '@/pages/chat/components/message-container'
import ChatMessage from '@/pages/chat/components/chat-message'
import ChatCta from '@/pages/chat/components/chat-cta'
import { getErrorMessage } from '@/lib/utils'

const TypingLoader: React.FC = () => (
  <div className="flex items-end justify-start">
    <div className="shrink-0 mr-2">
      <div className="bg-header-bg size-10 rounded-full overflow-hidden flex items-center justify-center">
        <img src={jasnaLogo} alt="Jasna" className="size-6 object-cover" />
      </div>
    </div>
    <div
      className="bg-header-bg rounded-xl inline-flex items-center justify-center"
      style={{ width: '52px', height: '32px' }}
    >
      <ReactLoading type="bubbles" color="#ffffff" height={35} width={36} />
    </div>
  </div>
)

const ChatDashboard: React.FC = () => {
  const dispatch = useAppDispatch()
  const [inputValue, setInputValue] = useState('')
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [defaultMessageReceived, setDefaultMessageReceived] = useState(false)
  const [emailInput, setEmailInput] = useState('')
  const [textInput, setTextInput] = useState('')
  const messageContainerRef = useRef<HTMLDivElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [sendMessageWithThread] = useSendMessageWithThreadMutation()
  const [threadId, setThreadId] = useState<string | null>(null)

  const { messages, isTyping } = useAppSelector((state) => state.chat)

  const [sendMessage] = useSendMessageMutation()

  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    window.parent.postMessage({ type: 'chat-open', isOpen: isChatOpen }, '*')
  }, [isChatOpen])

  useEffect(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current)
      resizeTimeoutRef.current = null
    }

    const sendResize = (width: number, height: number): void => {
      window.parent.postMessage(
        {
          type: 'resize-chat-frame',
          size: { width, height },
        },
        '*',
      )
    }

    if (isChatOpen) {
      sendResize(400, 600)
    } else if (isHovered) {
      sendResize(500, 130)
    } else {
      resizeTimeoutRef.current = setTimeout(() => {
        sendResize(130, 130)
      }, 500)
    }

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current)
      }
    }
  }, [isChatOpen, isHovered])

  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight
    }
  }, [messages])

  const handleSend = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    if (inputValue.trim() === '' || isTyping) return

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      type: MessageType.Sent,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    }

    dispatch(addMessage(userMessage))
    setInputValue('')
    dispatch(setTyping(true))

    try {
      if (!threadId) {
        const response = await sendMessage({ prompt: inputValue }).unwrap()
        setThreadId(response.threadId)

        const aiMessage: Message = {
          id: response.messageId,
          text: response.message || '',
          type: MessageType.Received,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          showInputFields: false,
        }

        dispatch(addMessage(aiMessage))
      } else {
        const response = await sendMessageWithThread({ threadId, prompt: inputValue }).unwrap()

        const aiMessage: Message = {
          id: response.messageId,
          text: response.message || '',
          type: MessageType.Received,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          showInputFields: false,
        }

        dispatch(addMessage(aiMessage))
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error)

      dispatch(
        addMessage({
          id: Date.now().toString(),
          text: errorMessage,
          type: MessageType.Received,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        }),
      )

      console.error('Error sending message:', error)
    } finally {
      dispatch(setTyping(false))
    }
  }

  const handleFormSubmit = (): void => {}

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && !e.shiftKey && !isTyping) {
      e.preventDefault()
      handleSend(e)
    }
  }

  const toggleChat = (): void => {
    setIsChatOpen(!isChatOpen)
    if (!defaultMessageReceived) {
      const aiMessage: Message = {
        id: 'welcomeMessageId',
        text:
          'Dobar dan,\n' +
          `Ja sam Jasna, digitalni asistent Ministarstva građevinarstva, saobraćaja i infrastrukture.

Ovde sam da Vam pomognem sa informacijama i servisnim podacima o radu Ministarstva, procedurama i uslugama.

Ne mogu da pokrećem ili rešavam pojedinačne predmete, ali mogu da Vas uputim kako da postupate i kome da se obratite.

Trenutno se nalazim u testnoj fazi i molim Vas da imate razumevanja ako neki od mojih odgovora nije tačan.

Nastaviću da učim uz Vašu pomoć.

Zato, izvolite, postavite Vaše pitanje.`,
        type: MessageType.Received,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        showInputFields: false,
      }

      dispatch(addMessage(aiMessage))
      setDefaultMessageReceived(true)
    }
    setIsHovered(false)
  }

  const startNewConversation = (): void => {
    dispatch(clearMessages())
    const aiMessage: Message = {
      id: 'welcomeMessageId',
      text:
        'Dobar dan,\n' +
        `Ja sam Jasna, digitalni asistent Ministarstva građevinarstva, saobraćaja i infrastrukture.

Ovde sam da Vam pomognem sa informacijama i servisnim podacima o radu Ministarstva, procedurama i uslugama.

Ne mogu da pokrećem ili rešavam pojedinačne predmete, ali mogu da Vas uputim kako da postupate i kome da se obratite.

Trenutno se nalazim u testnoj fazi i molim Vas da imate razumevanja ako neki od mojih odgovora nije tačan.

Nastaviću da učim uz Vašu pomoć.

Zato, izvolite, postavite Vaše pitanje.`,
      type: MessageType.Received,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      showInputFields: false,
    }

    dispatch(addMessage(aiMessage))
    setThreadId(null)
  }

  return (
    <>
      {isChatOpen && (
        <div className="fixed md:bottom-16 md:right-4 bottom-0 right-0 bg-white md:w-[460px] md:h-[640px] 2xl:h-[800px] size-full flex flex-col overflow-hidden border-[0.1px] border-blue-900 shadow-sm md:rounded-xl z-20">
          <div
            className="bg-header-bg w-full flex items-center justify-between py-4 px-5 h-[76px]"
            style={{ position: 'sticky', top: 0, zIndex: 10 }}
          >
            <div className="flex items-center gap-2">
              <img className="w-[37px]" src={jasnaLogo} alt="Jasna Logo" />
              <p className="text-white text-[18px]">Јасна</p>
            </div>
            <div className="flex items-center gap-4">
              <div
                className="bg-white flex items-center justify-center gap-2 p-2 rounded-3xl w-[147px] cursor-pointer"
                onClick={startNewConversation}
              >
                <p className="text-[14px] text-header-bg">Нови разговор</p>
                <img src={messagesIcon} alt="New conversation" />
              </div>
              <img src={closeIcon} className="cursor-pointer" onClick={toggleChat} alt="Close" />
            </div>
          </div>
          <div
            ref={messageContainerRef}
            className="flex-1 overflow-y-auto bg-white p-4"
            style={{ paddingTop: '70px' }}
          >
            {messages.length > 0 && (
              <div className="flex items-center w-full my-6">
                <div className="border-t border-[#C1C1C1]/50 grow"></div>
                <div className="px-4 text-[#1F519B] text-[10px] font-medium">
                  {messages[0]?.timestamp ||
                    new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
                <div className="border-t border-[#C1C1C1]/50 grow"></div>
              </div>
            )}
            <div className="flex flex-col gap-4">
              {messages.map((message: Message) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  email={emailInput}
                  userMessage={textInput}
                  onEmailChange={setEmailInput}
                  onMessageChange={setTextInput}
                  onSubmit={handleFormSubmit}
                />
              ))}

              {isTyping && <TypingLoader />}
            </div>
          </div>
          <div className="border-t border-gray-200">
            <MessageContainer
              disabled={isTyping}
              inputValue={inputValue}
              onSubmit={handleSend}
              onInputChange={(value) => setInputValue(value)}
              handleKeyDown={handleKeyDown}
            />
          </div>
        </div>
      )}
      {!isChatOpen && <ChatCta onClick={toggleChat} isHovered={isHovered} setIsHovered={setIsHovered} />}
    </>
  )
}

export default ChatDashboard
